import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Phone, Mail, ArrowUp, Facebook, Instagram } from 'lucide-react';
import { hotelInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { PAGES, langPath, pageLabel } from '../utils/siteNav';
import { guideLabels } from '../i18n/guides/meta';
import { legalTitle } from '../i18n/legal/meta';

// TikTok icon component (not available in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/>
  </svg>
);

const Footer = () => {
  const { t, language } = useLanguage();

  // Social media links
  const socialLinks = [
    {
      name: 'Facebook',
      url: 'https://www.facebook.com/umapremium/',
      icon: Facebook
    },
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/hotelumapremium/',
      icon: Instagram
    },
    {
      name: 'TikTok',
      url: 'https://www.tiktok.com/@umapremium',
      icon: TikTokIcon
    },
    {
      name: 'WhatsApp',
      url: 'https://wa.me/38268188816',
      icon: WhatsAppIcon,
      isWhatsapp: true
    }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    ...PAGES.map((p) => ({ name: p.slug === 'rooms' ? t('footer.roomsSuites') : pageLabel(p, t), href: langPath(language, p.slug), slug: p.slug })),
    { name: guideLabels(language).title, href: langPath(language, 'guides'), slug: 'guides' },
  ];

  const legalLinks = [
    { name: t('footer.privacyPolicy'), href: langPath(language, 'privacy-policy'), slug: 'privacy-policy' },
    { name: t('footer.termsConditions'), href: langPath(language, 'terms'), slug: 'terms' },
    { name: t('footer.cookiePolicy'), href: langPath(language, 'cookie-policy'), slug: 'cookie-policy' },
    { name: t('footer.cancellationPolicy'), href: `${langPath(language, 'terms')}#cancellation`, slug: 'cancellation' },
    { name: legalTitle(language, 'imprint'), href: langPath(language, 'imprint'), slug: 'imprint' },
  ];

  return (
    <footer className="bg-slate-950 pt-20 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 pb-12 border-b border-slate-800">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <h3 className="text-2xl font-serif text-white mb-2">{hotelInfo.name}</h3>
              <div className="flex items-center gap-1">
                {[...Array(hotelInfo.rating)].map((_, i) => (
                  <Star key={`footer-star-${i}`} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
                <span className="text-slate-400 text-sm ml-2">{t('footer.starHotel')}</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center text-slate-400 hover:text-white transition-all duration-300 ${
                    social.isWhatsapp ? 'hover:bg-[#25D366]' : 'hover:bg-amber-500'
                  }`}
                  aria-label={social.isWhatsapp ? 'Chat with us on WhatsApp' : `Follow us on ${social.name}`}
                  data-testid={`social-${social.name.toLowerCase()}`}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.slug}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-amber-400 transition-colors duration-300 text-sm"
                    data-testid={`footer-link-${link.slug}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-semibold mb-6">{t('footer.legal')}</h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.slug}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-amber-400 transition-colors duration-300 text-sm"
                    data-testid={`footer-legal-${link.slug}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-6">{t('footer.contactUs')}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">{hotelInfo.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <a href={`tel:${hotelInfo.phone}`} className="text-slate-400 hover:text-amber-400 transition-colors text-sm">
                  {hotelInfo.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <a href={`mailto:${hotelInfo.email}`} className="text-slate-400 hover:text-amber-400 transition-colors text-sm">
                  {hotelInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 gap-4">
          <p className="text-slate-400 text-sm">
            &copy; {currentYear} {hotelInfo.name}. {t('footer.allRights')}
          </p>
          <button
            onClick={scrollToTop}
            className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors duration-300 text-sm group"
          >
            {t('footer.backToTop')}
            <ArrowUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform duration-300" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
